import { JsonLd } from 'components/common/JsonLd';
import { imageUrlBuilder } from 'lib/sanity';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import type { BlogPost } from 'types/cms/blog/BlogPost';
import {
  articleId,
  buildArticle,
  buildPodcastEpisode,
  podcastEpisodeId,
} from 'utils/jsonld';

interface BlogPostJsonLdProps {
  post: BlogPost;
}

export function BlogPostJsonLd({ post }: BlogPostJsonLdProps) {
  const router = useRouter();

  const isPodcastType = post.type === 'timeline_podcast';

  const schema = useMemo(() => {
    if (isPodcastType) {
      // Skip emission rather than misrepresent a podcast as an Article
      // when episode data is missing or its audio is empty (broken/draft
      // CMS content). An AudioObject with an empty contentUrl is an
      // invalid PodcastEpisode payload.
      if (!post.episode || !post.episode.audio) return null;
      return buildPodcastEpisode({
        episode: post.episode,
        summary: post.summary,
        publishedAt: post.publishedAt,
        contributors: post.contributors,
        pagePath: router.asPath,
      });
    }
    const heroImageUrl = imageUrlBuilder
      .image(post.heroImage)
      .width(1200)
      .height(1200 / (16 / 9))
      .fit('crop')
      .url();
    return buildArticle({
      post,
      pagePath: router.asPath,
      heroImageUrl,
    });
  }, [post, router.asPath, isPodcastType]);

  if (!schema) return null;

  const id = isPodcastType
    ? podcastEpisodeId(router.asPath)
    : articleId(router.asPath);

  return <JsonLd scriptKey={id} schema={schema} />;
}
